import { Processor } from '.';
import { Node } from '../Node';
import { Text } from '../component';
import { ComponentInnerState } from './Processor';

interface TextInnerState extends ComponentInnerState {
	text?: string;
	color?: string;
	fontSize?: number;
	fontFamily?: string;
	fontWeight?: string;
	fontStyle?: string;
	border?: number;
	backgroundColor?: string;
	borderColor?: string;
	borderRadius?: number;
	padding?: number | Array<number>;
	alignment?: 'left' | 'center' | 'right';
}

export class TextProcessor extends Processor {
	public start(component: Text, node: Node): boolean {
		const iState: TextInnerState = new Object();
		const {
			text,
			color,
			fontSize,
			fontFamily,
			fontWeight,
			fontStyle,
			border,
			backgroundColor,
			borderColor,
			borderRadius,
			padding,
			alignment,
		} = component.state;

		const config = {
			text,
			color,
			fontSize,
			fontFamily,
			fontWeight,
			fontStyle,
			border,
			backgroundColor,
			borderColor,
			borderRadius,
			padding,
			alignment,
		};

		iState.meshAssembly = this.engine.meshFactory.textBuilder.createText(config, 'Text');

		Object.assign(iState, config);

		node.addChildAssembly(iState.meshAssembly);

		this.setState(component, iState);
		return true;
	}

	public enable(component: Text): boolean {
		const iState = this.getState<TextInnerState>(component);
		if (!iState.meshAssembly || !iState.meshAssembly.isReady() || component.isDisabled) {
			return false;
		}
		iState.meshAssembly.localPosition = component.state.position;
		this.engine.meshFactory.textBuilder.shouldRenderText(iState.meshAssembly, true);
		return true;
	}

	public disable(component: Text): boolean {
		const assembly = this.getState<TextInnerState>(component).meshAssembly;
		if (assembly) {
			this.engine.meshFactory.textBuilder.shouldRenderText(assembly, false);
			return true;
		}

		return false;
	}

	public onDestroy(component: Text): boolean {
		const iState = this.getState<TextInnerState>(component);
		if (iState.meshAssembly) {
			iState.meshAssembly.shouldRender = false;
			this.engine.meshFactory.textBuilder.destroyText(iState.meshAssembly);
			iState.meshAssembly.destroy();
			this.deleteState(component);
			return true;
		}

		return false;
	}

	public updateState(component: Text, node: Node): boolean {
		const iState = this.getState<TextInnerState>(component);
		if (iState.meshAssembly) {
			if (!this.isEqual(iState, component.state)) {
				const {
					text,
					color,
					fontSize,
					fontFamily,
					fontWeight,
					fontStyle,
					border,
					backgroundColor,
					borderColor,
					borderRadius,
					padding,
					alignment,
				} = component.state;

				const config = {
					text,
					color,
					fontSize,
					fontFamily,
					fontWeight,
					fontStyle,
					border,
					backgroundColor,
					borderColor,
					borderRadius,
					padding,
					alignment,
				};
				this.engine.meshFactory.textBuilder.modifyText(iState.meshAssembly, config);
				Object.assign(iState, config);
			}
			node.addChildAssembly(iState.meshAssembly);
			iState.meshAssembly.localPosition = component.state.position;
			return true;
		}

		return false;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private isEqual(a: any, b: any): boolean {
		return Object.keys(a).every((k) => k == 'meshAssembly' || a[k] == b[k]);
	}
}
